// © Microsoft Corporation. All rights reserved.

import { AppPerfTraceConfig, tryParseAppTraceConfig } from "./AppPerfTraceConfig";
import { PageMeasure, PerfTimeline, TryGetPerfTimelineFunc, createPerfTimeline, setTryGetPerfTimelineFunc } from "./PerfTimeline";

import { PerfTimelineImpl } from "./PerfTimelineImpl";
import { logger } from "../logging";

let configOverrides: AppPerfTraceConfig;

/**
 * The configuration for app trace switches. This will be updated with the
 * config in the CMS and any properties from URL overrides.
 */
const config: AppPerfTraceConfig = {};

/**
 * Dictionary containing the unique {PerfTimeline} object instances across the app.
 * When related measurements on the same perf timeline is required across unrelated parts
 * of the code, this will guarantee you're tracking those measurements under the same
 * perf timeline using a combination of the experience name and instance id.
 */
let perfTimelineInstances: { [instanceId: string]: PerfTimeline };

/**
 * Initializes experience perf diagnostic specific state.
 * @param locationHref - the value of window.location.href from which to parse options.
 * @param tryGetPerfTimelineFunc - the injected dependency for perf timeline factory method.
 */
export function initExperienceDiagnosticsCore(locationHref: string, tryGetPerfTimelineFunc: TryGetPerfTimelineFunc): void {
    perfTimelineInstances = {};

    // Use the actual function to get perf timeline when initExperienceDiagnostics is called.
    setTryGetPerfTimelineFunc(tryGetPerfTimelineFunc);

    setAppPerfTraceConfigOverrides(locationHref);
}

/**
 * Returns the app trace config.
 */
export function getAppPerfTraceConfig(): AppPerfTraceConfig {
    return config;
}

/**
 * Helper to set the app trace config dynamically. Useful when the app trace config
 * has been loaded via a configuration (such as entry point config), and we want to use
 * that as the default/base config instead. Note that we still apply higher precedence
 * to the location parsed config since we want to make sure to use those if specified.
 * @param baseConfig the base configuration to apply.
 */
export function setAppPerfTraceBaseConfig(baseConfig: Partial<AppPerfTraceConfig>): void {
    if (!baseConfig) {
        return;
    }

    // Use the config from URL last, so we have highest precedence on QSPs
    Object.assign(config, baseConfig, configOverrides);

    // forcefully disable for some options we never want enabled in upload scenario
    if (config.uploadTrace) {

        // don't bloat page timings with app trace related timings
        config.addPageTimings = false;

        // don't emit performance marks that show up on F12
        config.addPerformanceMarks = false;

        // if sample rate has not been set, set to 0, or does not pass
        // the random value test, we need to:
        //  1. disable app trace collection completely
        //  2. disable upload flag, so that when we try to get the app trace for upload
        //     it will simply return an empty trace, and prevent the upload.
        //
        if (!config.sampleRate
            || Math.random() > config.sampleRate) {

            config.enablePerfTimeline = false;
            config.uploadTrace = false;
        }
    }
}

/**
 * Initializes experience perf diagnostic specific state
 * Note: this method should not be called explicitly (other than from UTs.)
 *      It is already called by default at the beginning of module initialization.
 * @param locationHref - the value of window.location.href from which to parse options
 */
function setAppPerfTraceConfigOverrides(locationHref: string): void {
    Object.assign(config, configOverrides = tryParseAppTraceConfig(locationHref));
}

/**
 * Gets or creates a perf timeline object for the given experience instance if perf measurements are enabled
 * for the given experience.
 * @param experienceName - the name of the experience for which to create the timeline.
 * @param instanceId - the ID that will uniquely identify this timeline across multiple instances.
 */
export const tryGetPerfTimelineImpl: TryGetPerfTimelineFunc = (experienceName: string, instanceId: string, async?: boolean): PerfTimeline => {
    return shouldTrackPerfTimeline(experienceName)
        ? getPerfTimeline(experienceName, instanceId, async)
        : undefined;
};

/**
 * Gets or creates a perf timeline object for the given experience instance. This should only be used in special
 * circumstances where we may not know in advance whether perf timeline should be enabled for an experience, but
 * still want to start the measurements as not to miss anything in case collection is enabled at a later time.
 * This is usually used in entry point to collect intialization measurements before the config is loaded with
 * more parameters for app perf trace collection in order to upload trace logs in production.
 * @param experienceName - the name of the experience for which to get the perf timeline.
 * @param instanceId - the instance specific ID that will uniquely identify this instance from other instances.
 */
export function getPerfTimeline(experienceName: string, instanceId: string, async?: boolean): PerfTimeline {
    const instanceKey = getInstanceKeyCore(experienceName, instanceId);

    const perfTimeline = perfTimelineInstances[instanceKey];
    if (perfTimeline) {
        return perfTimeline;
    }

    return perfTimelineInstances[instanceKey] = createPerfTimeline(
        PerfTimelineImpl,
        experienceName,
        instanceId,
        config.addPerformanceMarks,
        config.addPageTimings,
        async
    );
}

/**
 * Renames the given perf timeline object and moves the lookup dictionary reference under the new key
 * @param perfTimeline - the perf timeline to rename
 * @param newExperienceName - the new name for the perf timeline
 */
export function renamePerfTimeline(perfTimeline: PerfTimeline, newExperienceName: string): void {

    const oldInstanceKey = getInstanceKey(perfTimeline);

    if (perfTimelineInstances[oldInstanceKey] !== perfTimeline) {
        logger.logError(`Attempted to rename a perf timeline object with mismatching instance key ${oldInstanceKey}`);
        return;
    }

    // rename the perf timeline to the new name
    perfTimeline.rename(newExperienceName);

    // delete the old reference
    delete perfTimelineInstances[oldInstanceKey];

    // ...and add the new instance under the new key
    const newInstanceKey = getInstanceKey(perfTimeline);
    perfTimelineInstances[newInstanceKey] = perfTimeline;
}

/**
 * Returns the list of page measures from all the perf timeline objects being tracked
 */
export function getExperienceMeasures(): Array<PageMeasure> {
    const pageMeasures: Array<PageMeasure> = [];
    Object.keys(perfTimelineInstances).forEach(instanceKey =>
        pageMeasures.push(...perfTimelineInstances[instanceKey].getPageMeasures())
    );
    return pageMeasures;
}

/**
 * Helper to check whether we should track perf timeline for the given experience
 * @param experienceName - the name of the experience to check
 * @returns {boolean} indicating whether the given experience should be tracked for perf
 */
function shouldTrackPerfTimeline(experienceName: string): boolean {
    if (!config.enablePerfTimeline) {
        return false;
    }

    // if inclusion list is present, it will supercede the exclusion list,
    // so return true early if a match is found here.
    const { experiencesForPerf } = config;
    if (experiencesForPerf && experiencesForPerf[experienceName]) {
        return true;
    }

    // check for exclusion list
    const { experiencesExcludedForPerf } = config;
    if (experiencesExcludedForPerf && experiencesExcludedForPerf[experienceName]) {
        return false;
    }

    // otherwise the final fallback will be the track all flag.
    return config.trackAllExp;
}

/**
 * Gets the instance key to store the experience timeline
 * @param perfTimeline - the timeline object from which to get the instance key.
 * @returns the cache key that represents the location to which to store the perf timeline.
 */
function getInstanceKey(perfTimeline: PerfTimeline): string {
    const { timelineName, instanceId } = perfTimeline;
    return getInstanceKeyCore(timelineName, instanceId);
}

/**
 * Gets the instance key to store the experience timeline
 * @param experienceName - the name of the experience
 * @param instanceId - the instance ID for the perf timeline.
 * @returns the cache key that represents the location to which to store the perf timeline.
 */
function getInstanceKeyCore(experienceName: string, instanceId: string): string {
    return `${experienceName}-${instanceId || "0"}`;
}